import type { Settings } from "@go-novus/admin-api";

import type { WidgetAppearance } from "./config";

/**
 * 商家后台配置里能决定外观的两块，外加 Logo。
 * Logo 不在 `Settings` 里：图片不走那份 JSON，由服务端另外算出地址。
 */
export type AppearanceSource = Pick<Settings, "agent" | "chatbox"> & {
  logoUrl?: string | null;
};

/** 三档字体对应的字体栈。`theme` 借宿主页面的字。 */
const FONT_STACK: Record<Settings["chatbox"]["font"], string> = {
  theme: "inherit",
  sans: "system-ui, sans-serif",
  serif: "Georgia, serif",
};

/** 毛玻璃开着时的模糊半径与面板不透明度；关着就是全实底。 */
const GLASS_BLUR_PX = 18;
const GLASS_SURFACE_OPACITY = 0.74;

/**
 * 商家后台配置到挂件外观的唯一一处映射。店面挂件和后台 Settings 的预览共用它，
 * 谁都不许再抄一份——抄了预览就会和店面慢慢长歪。
 */
export function readAppearance({
  agent,
  chatbox,
  logoUrl,
}: AppearanceSource): WidgetAppearance {
  return {
    accent: chatbox.themeColor,
    // Logo 只作为聊天头部的独立品牌标识。
    avatar: logoUrl ?? undefined,
    blur: chatbox.glass ? GLASS_BLUR_PX : 0,
    character: chatbox.character,
    direction: chatbox.direction,
    font: FONT_STACK[chatbox.font],
    fontSize: `${String(chatbox.fontSize)}px`,
    launcherDraggable: chatbox.launcherDraggable,
    position: chatbox.position === "bottom_left" ? "left" : "right",
    proactivePromptStyle: chatbox.proactivePromptStyle,
    radius: chatbox.radius,
    replyCopyButton: chatbox.replyCopyButton,
    // 后台预览喂的是草稿，刚点「添加」的那一行还是空的，不能画成一张空白卡片。
    suggestions: chatbox.suggestedQuestions.filter(
      (question) => question.trim() !== "",
    ),
    surface: chatbox.surface,
    surfaceOpacity: chatbox.glass ? GLASS_SURFACE_OPACITY : 1,
    title: agent.name,
    welcomeMessage: chatbox.welcomeMessage,
  };
}
